// Browser-tab signal — document.title + favicon dot. Covers the designer
// case: "GC switches to email mid-dictation and forgets. The mic is still
// on, and the tab says so."
//
// Favicon: canvas-draw a 32×32 square (the ink colour) with a red dot
// bottom-right, encode to data URL, swap the <link rel="icon"> href.
// Reverts to the baseline (sidebar glyph) when state leaves live/paused.

const FAVICON_SIZE = 32;

let originalTitle: string | null = null;
let originalFaviconHref: string | null = null;

export function setTabSignal(state: 'live' | 'paused' | 'off', title: string): void {
  if (originalTitle === null) originalTitle = document.title;
  const link = ensureFaviconLink();
  if (originalFaviconHref === null) originalFaviconHref = link.href;

  if (state === 'off') {
    document.title = originalTitle;
    link.href = originalFaviconHref;
    return;
  }

  document.title = title;
  link.href = drawFavicon(state);
}

function ensureFaviconLink(): HTMLLinkElement {
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  return link;
}

function drawFavicon(state: 'live' | 'paused'): string {
  const canvas = document.createElement('canvas');
  canvas.width = FAVICON_SIZE;
  canvas.height = FAVICON_SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) return originalFaviconHref ?? '';

  // Base glyph — plain filled rounded square in the ink colour, so the
  // app's identity still reads at tab-size.
  ctx.fillStyle = '#0A0B14';
  roundedRect(ctx, 0, 0, FAVICON_SIZE, FAVICON_SIZE, 6);
  ctx.fill();

  // Status dot bottom-right. Red for live, amber for paused.
  const dotColour = state === 'live' ? '#C42B1F' : '#B86A0F';
  ctx.fillStyle = dotColour;
  ctx.beginPath();
  ctx.arc(FAVICON_SIZE - 8, FAVICON_SIZE - 8, 7, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = '#FBF8F0';
  ctx.lineWidth = 2;
  ctx.stroke();

  return canvas.toDataURL('image/png');
}

function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}
